'use client';

import { useEffect, useMemo, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient, type UserBet } from '@/lib/api';
import { categoryLabel, formatUsdt, pluralizeRu } from '@/lib/format';
import { getInitData } from '@/lib/tma';

const CATEGORY_BG: Record<string, string> = {
  elections: 'bg-theme-elections',
  conflicts: 'bg-theme-conflicts',
  diplomacy: 'bg-theme-diplomacy',
  sanctions: 'bg-theme-sanctions',
  domestic: 'bg-theme-domestic',
  geopolitics: 'bg-theme-geopolitics',
};

export function ActivityCard({ refreshKey }: { refreshKey: number }) {
  const [bets, setBets] = useState<UserBet[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const api = createApiClient(getInitData);
    api
      .myBets(200)
      .then((r) => setBets(r.bets))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        console.error('ActivityCard failed', e?.code);
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const data = useMemo(() => {
    if (!bets || bets.length === 0) return null;

    // Categories: count + volume per category.
    const catMap = new Map<string, { count: number; volumeMicro: number }>();
    let totalVolumeMicro = 0;
    let yesCount = 0;
    let noCount = 0;
    for (const b of bets) {
      const ent = catMap.get(b.market_category) ?? { count: 0, volumeMicro: 0 };
      ent.count += 1;
      ent.volumeMicro += b.amount_micro;
      catMap.set(b.market_category, ent);
      totalVolumeMicro += b.amount_micro;
      if (b.side === 1) yesCount += 1;
      else noCount += 1;
    }
    const categories = Array.from(catMap.entries())
      .map(([key, v]) => ({
        key,
        count: v.count,
        volumeMicro: v.volumeMicro,
        pct: totalVolumeMicro > 0 ? (v.volumeMicro / totalVolumeMicro) * 100 : 0,
      }))
      .sort((a, b) => b.volumeMicro - a.volumeMicro);

    // 7 days activity buckets.
    const days = last7Days();
    const dayMap = new Map<string, number>();
    for (const d of days) dayMap.set(d.key, 0);
    for (const b of bets) {
      const k = dayKey(new Date(b.created_at * 1000));
      if (dayMap.has(k)) dayMap.set(k, (dayMap.get(k) ?? 0) + 1);
    }
    const dayBars = days.map((d) => ({ ...d, count: dayMap.get(d.key) ?? 0 }));
    const maxDayCount = Math.max(1, ...dayBars.map((b) => b.count));
    const total7d = dayBars.reduce((s, b) => s + b.count, 0);

    const total = yesCount + noCount;
    const yesPct = total > 0 ? (yesCount / total) * 100 : 50;

    return { categories, dayBars, maxDayCount, total7d, yesCount, noCount, yesPct };
  }, [bets]);

  if (loading) {
    return (
      <div className="bg-bg-elev p-5 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-pink">
      <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em] mb-4">
        ● ТВОЯ АКТИВНОСТЬ
      </div>

      {/* Categories */}
      <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-2">
        ГДЕ СТАВИШЬ
      </div>
      <div className="space-y-2 mb-5">
        {data.categories.map((c) => (
          <div key={c.key} className="text-[11px]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-text-secondary">{categoryLabel(c.key)}</span>
              <span className="font-mono text-text-tertiary tabular-nums tracking-[0.05em]">
                {c.count}{' '}
                {pluralizeRu(c.count, 'ставка', 'ставки', 'ставок')} ·{' '}
                {formatUsdt(c.volumeMicro)} · {Math.round(c.pct)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-bg-elev2 overflow-hidden">
              <div
                className={`h-full ${CATEGORY_BG[c.key] ?? 'bg-text-tertiary'} transition-all`}
                style={{ width: `${c.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* YES/NO bias */}
      <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-2">
        СТОРОНА
      </div>
      <div className="mb-5">
        <div className="flex justify-between mb-1.5">
          <span className="text-yes font-bold tabular-nums display text-[14px]">
            YES {Math.round(data.yesPct)}%
          </span>
          <span className="text-no font-bold tabular-nums display text-[14px]">
            NO {Math.round(100 - data.yesPct)}%
          </span>
        </div>
        <div className="w-full h-2 bg-bg-elev2 overflow-hidden flex">
          <div className="bg-yes" style={{ width: `${data.yesPct}%` }} />
          <div className="bg-no" style={{ width: `${100 - data.yesPct}%` }} />
        </div>
        <p className="font-mono text-[10px] text-text-tertiary mt-1.5 tracking-[0.05em]">
          {data.yesCount} YES · {data.noCount} NO СТАВОК
        </p>
      </div>

      {/* 7-day chart */}
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em]">
          ЗА 7 ДНЕЙ
        </div>
        <div className="font-mono text-[10px] text-accent tabular-nums tracking-[0.05em]">
          {data.total7d} {pluralizeRu(data.total7d, 'СТАВКА', 'СТАВКИ', 'СТАВОК')}
        </div>
      </div>
      <div className="flex items-end justify-between gap-1 h-14">
        {data.dayBars.map((b) => {
          const pct = (b.count / data.maxDayCount) * 100;
          return (
            <div key={b.key} className="flex flex-col items-center justify-end flex-1 h-full">
              <div className="font-mono text-[9px] text-text-tertiary tabular-nums mb-0.5 leading-none">
                {b.count > 0 ? b.count : ''}
              </div>
              <div
                className={`w-full ${b.count > 0 ? 'bg-accent' : 'bg-bg-elev2'} transition-all`}
                style={{ height: `${Math.max(pct, 5)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between gap-1 mt-1">
        {data.dayBars.map((b) => (
          <div
            key={b.key}
            className="flex-1 text-center font-mono text-[10px] text-text-tertiary tracking-[0.05em]"
          >
            {b.label.toUpperCase()}
          </div>
        ))}
      </div>
    </div>
  );
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

const DAY_LABELS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

function last7Days(): Array<{ key: string; label: string }> {
  const days: Array<{ key: string; label: string }> = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    days.push({
      key: dayKey(d),
      label: DAY_LABELS[d.getDay()] ?? '',
    });
  }
  return days;
}
